import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  AreaChart,
  Area,
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { ArrowLeft, Globe, MapPin, User, Calendar, Building2, TrendingUp, Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StatusBadge, CompanyAvatar } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BOLAG,
  KOMMUNER,
  MOGNADSSKALA,
  formatSEK,
  totalKapital,
  offentligaTyper,
  rlDelta,
  type Bolag,
  type Kommun,
} from "@/lib/portfolio-data";
import { getKommunOverride, setKommunOverride } from "@/lib/bolag-overrides";

export const Route = createFileRoute("/bolag/$id")({
  head: ({ params }) => {
    const b = BOLAG.find((x) => x.id === params.id);
    return {
      meta: [
        { title: b ? `${b.namn} — Inkubera` : "Bolag — Inkubera" },
        { name: "description", content: b?.beskrivning ?? "Bolagsdetalj" },
      ],
    };
  },
  loader: ({ params }) => {
    const b = BOLAG.find((x) => x.id === params.id);
    if (!b) throw notFound();
    return b;
  },
  component: BolagDetalj,
  notFoundComponent: () => (
    <AppShell>
      <div className="p-12 text-center">
        <h1 className="font-display text-3xl">Bolaget hittades inte</h1>
        <Link to="/portfolj" className="text-sm text-muted-foreground hover:text-foreground mt-4 inline-block">
          ← Tillbaka till portföljen
        </Link>
      </div>
    </AppShell>
  ),
});

function BolagDetalj() {
  const b = Route.useLoaderData() as Bolag;
  const [kommun, setKommun] = useState<Kommun>(
    (getKommunOverride(b.id) ?? b.kommun ?? "Örebro") as Kommun,
  );
  const kapital = totalKapital(b);
  const mognadIdx = MOGNADSSKALA.indexOf(b.mognad);

  // Radar
  const radarData = [
    { axis: "Produkt", v: Math.min(100, (mognadIdx + 1) * 14) },
    { axis: "Marknad", v: b.hälsoscore - 10 },
    { axis: "Team", v: Math.min(100, b.anstallda * 4 + 40) },
    { axis: "Finansiering", v: Math.min(100, kapital / 600_000) },
    { axis: "Tillväxt", v: b.hälsoscore },
    { axis: "Hållbarhet", v: 60 + ((b.namn.length * 7) % 30) },
  ];

  // Cumulative kapital
  const sorted = [...b.finansiering].sort((a, c) => +new Date(a.datum) - +new Date(c.datum));
  let acc = 0;
  const kapitalÖverTid = sorted.map((f) => {
    acc += f.belopp / 1_000_000;
    return { datum: new Date(f.datum).toLocaleDateString("sv-SE", { month: "short", year: "2-digit" }), Mkr: +acc.toFixed(2) };
  });

  return (
    <AppShell>
      <div className="px-8 pt-6">
        <Link to="/portfolj" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-3.5" /> Portfölj
        </Link>
      </div>

      <section className="px-8 pt-6 pb-8 border-b border-border">
        <div className="flex items-start gap-5">
          <CompanyAvatar initialer={b.initialer} farg={b.logoFarg} size={80} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-4xl">{b.namn}</h1>
              <StatusBadge fas={b.fas} size="md" />
            </div>
            <p className="text-muted-foreground mt-2 max-w-2xl">{b.beskrivning}</p>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Globe className="size-3.5" /> {b.webb}</span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="size-3.5" /> Säte: {kommun} kommun
                <EditKommunDialog
                  current={kommun}
                  onSave={(k) => {
                    setKommunOverride(b.id, k);
                    setKommun(k);
                    b.kommun = k;
                    toast.success(`Säte uppdaterat till ${k} kommun`);
                  }}
                />
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="size-3.5" /> Inflöde: {b.inflöde ?? "—"}
              </span>
              <span className="inline-flex items-center gap-1.5"><User className="size-3.5" /> {b.affarsutvecklare}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="size-3.5" /> Sedan {new Date(b.startdatum).toLocaleDateString("sv-SE")}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {b.branscher.map((br) => (
                <span key={br} className="text-xs px-2.5 py-1 rounded-full bg-secondary">{br}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Totalt kapital" value={formatSEK(kapital)} />
        <Stat label="Hälsoscore" value={`${b.hälsoscore}/100`} />
        <Stat label="Omsättning" value={`${b.omsattning.toLocaleString("sv-SE")} tkr`} />
        <Stat label="Anställda" value={b.anstallda} />
      </section>

      <section className="px-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-card border border-border p-5">
          <h3 className="font-display text-lg">Mognadsgrad</h3>
          <p className="text-xs text-muted-foreground">Aktuell: {b.mognad}</p>
          <div className="mt-5 space-y-1.5">
            {MOGNADSSKALA.map((m, i) => (
              <div key={m} className="flex items-center gap-3">
                <span className={`text-xs w-40 ${i <= mognadIdx ? "text-foreground" : "text-muted-foreground/60"}`}>{m}</span>
                <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: i <= mognadIdx ? "100%" : "0%",
                      background: i === mognadIdx ? "var(--accent)" : "var(--foreground)",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-card border border-border p-5">
          <h3 className="font-display text-lg">Utvecklings­profil</h3>
          <p className="text-xs text-muted-foreground">Bedömning över sex dimensioner</p>
          <div className="h-64 mt-2">
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="var(--border)" />
                <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                <Radar dataKey="v" stroke="var(--foreground)" fill="var(--accent)" fillOpacity={0.4} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <RLSection b={b} />

      <section className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl bg-card border border-border p-5">
          <h3 className="font-display text-lg">Ackumulerat kapital</h3>
          <p className="text-xs text-muted-foreground">Mkr över tid</p>
          {kapitalÖverTid.length > 0 ? (
            <div className="h-56 mt-3">
              <ResponsiveContainer>
                <AreaChart data={kapitalÖverTid} margin={{ left: -10, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="gKap" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--foreground)" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="var(--foreground)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="datum" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} unit=" Mkr" />
                  <Tooltip contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }} />
                  <Area type="monotone" dataKey="Mkr" stroke="var(--foreground)" strokeWidth={2} fill="url(#gKap)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">Ingen finansiering registrerad än</div>
          )}
        </div>

        <div className="rounded-2xl bg-card border border-border p-5">
          <h3 className="font-display text-lg">Finansieringshistorik</h3>
          <ul className="mt-3 space-y-3">
            {[...b.finansiering].sort((a, c) => +new Date(c.datum) - +new Date(a.datum)).map((f, i) => (
              <li key={i} className="flex items-start gap-3 pb-3 border-b border-border last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{f.investerare ?? f.typ}</div>
                  <div className="text-xs text-muted-foreground">{f.typ} · {new Date(f.datum).toLocaleDateString("sv-SE")}</div>
                </div>
                <div className="font-display">{formatSEK(f.belopp)}</div>
              </li>
            ))}
            {b.finansiering.length === 0 && <li className="text-sm text-muted-foreground">Ingen historik</li>}
          </ul>
          <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
            {formatSEK(b.finansiering.filter((f) => offentligaTyper.includes(f.typ)).reduce((s, f) => s + f.belopp, 0))} offentligt stöd
          </div>
        </div>
      </section>

      <section className="px-8 py-6 pb-12">
        <div className="rounded-2xl bg-card border border-border p-5">
          <h3 className="font-display text-lg">Milstolpar</h3>
          <ol className="mt-4 relative border-l border-border pl-6 space-y-5">
            {[...b.milstolpar].sort((a, c) => +new Date(c.datum) - +new Date(a.datum)).map((m, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[27px] top-1 size-2.5 rounded-full bg-foreground ring-4 ring-background" />
                <div className="text-xs text-muted-foreground">{new Date(m.datum).toLocaleDateString("sv-SE")}</div>
                <div className="font-medium">{m.rubrik}</div>
                {m.beskrivning && <div className="text-sm text-muted-foreground">{m.beskrivning}</div>}
              </li>
            ))}
          </ol>
        </div>
      </section>
    </AppShell>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="p-4 rounded-2xl bg-card border border-border">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl mt-1.5">{value}</div>
    </div>
  );
}

function RLSection({ b }: { b: Bolag }) {
  const historik = b.rlHistorik ?? [];
  const delta = rlDelta(b);
  const senaste = historik[historik.length - 1];
  const data = historik.map((p) => ({
    datum: new Date(p.datum).toLocaleDateString("sv-SE", { month: "short", year: "2-digit" }),
    BRL: p.brl,
    MRL: p.mrl,
    SRL: p.srl,
    TRL: p.trl,
  }));

  return (
    <section className="px-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 rounded-2xl bg-card border border-border p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-lg flex items-center gap-2">
              <TrendingUp className="size-4" /> Readiness-Levels (RL) över tid
            </h3>
            <p className="text-xs text-muted-foreground">
              Bolagets förflyttning på BRL · MRL · SRL · TRL (skala 1–9)
            </p>
          </div>
          {delta && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Total RL-rörelse
              </div>
              <div className={`font-display text-2xl ${delta.total >= 0 ? "text-foreground" : "text-destructive"}`}>
                {delta.total >= 0 ? "+" : ""}{delta.total}
              </div>
            </div>
          )}
        </div>
        {data.length >= 2 ? (
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <LineChart data={data} margin={{ left: -10, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="datum" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 9]} stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="BRL" stroke="oklch(0.65 0.18 250)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="MRL" stroke="oklch(0.7 0.16 50)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="SRL" stroke="oklch(0.7 0.16 140)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="TRL" stroke="oklch(0.65 0.2 320)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">
            Ingen RL-historik registrerad än
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-card border border-border p-5">
        <h3 className="font-display text-lg">Start vs nu</h3>
        <p className="text-xs text-muted-foreground">Förflyttning från inkubationsstart</p>
        {historik.length >= 2 ? (
          <div className="h-48 mt-2">
            <ResponsiveContainer>
              <RadarChart data={[
                { axis: "BRL", Start: historik[0].brl, Nu: senaste!.brl },
                { axis: "MRL", Start: historik[0].mrl, Nu: senaste!.mrl },
                { axis: "SRL", Start: historik[0].srl, Nu: senaste!.srl },
                { axis: "TRL", Start: historik[0].trl, Nu: senaste!.trl },
              ]}>
                <PolarGrid stroke="var(--border)" />
                <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                <Radar dataKey="Start" stroke="var(--muted-foreground)" fill="var(--muted-foreground)" fillOpacity={0.15} />
                <Radar dataKey="Nu" stroke="var(--foreground)" fill="var(--accent)" fillOpacity={0.45} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        ) : null}
        {senaste ? (
          <div className="mt-4 space-y-3">
            {([
              ["BRL", senaste.brl, "Business"],
              ["MRL", senaste.mrl, "Market"],
              ["SRL", senaste.srl, "Sustainability"],
              ["TRL", senaste.trl, "Technology"],
            ] as const).map(([k, v, label]) => (
              <div key={k}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium">{k} <span className="text-muted-foreground font-normal">· {label}</span></span>
                  <span className="font-display">{v}/9</span>
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full bg-foreground" style={{ width: `${(v / 9) * 100}%` }} />
                </div>
              </div>
            ))}
            {delta && (
              <div className="pt-3 mt-3 border-t border-border grid grid-cols-4 gap-2 text-center">
                {([["BRL", delta.brl], ["MRL", delta.mrl], ["SRL", delta.srl], ["TRL", delta.trl]] as const).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
                    <div className={`text-sm font-medium ${v >= 0 ? "text-foreground" : "text-destructive"}`}>
                      {v >= 0 ? "+" : ""}{v}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground mt-4">Ingen avläsning</div>
        )}
      </div>
    </section>
  );
}

function EditKommunDialog({
  current,
  onSave,
}: {
  current: Kommun;
  onSave: (k: Kommun) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<Kommun>(current);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setValue(current);
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="Redigera säte"
          className="ml-1 inline-flex items-center justify-center size-5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
        >
          <Pencil className="size-3" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Redigera bolagets säte</DialogTitle>
          <DialogDescription>
            Välj den kommun i Örebro län där bolaget har sitt geografiska säte.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="kommun-select">Kommun</Label>
          <Select value={value} onValueChange={(v) => setValue(v as Kommun)}>
            <SelectTrigger id="kommun-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KOMMUNER.map((k) => (
                <SelectItem key={k} value={k}>
                  {k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Avbryt
          </Button>
          <Button
            onClick={() => {
              onSave(value);
              setOpen(false);
            }}
          >
            Spara
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

